<template>
  <div class="card">
    <div class="card-body">
      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-header">
          <h4>
            <a-icon type="filter" class="filter-icon" />
            {{ $t("all.filtresEtRecherche") }}
          </h4>
          <div class="action-button-group">
            <a-button
              type="primary"
              @click="imprimer"
              :class="{
                disabledPack: !btnAccessGetter.includes('pEtatDesRecettes'),
              }"
              :loading="printLoading"
              class="action-button"
            >
              <a-icon type="printer" class="action-icon" />
              {{ $t("action.imprimer") }}
            </a-button>
          </div>
        </div>

        <a-row :gutter="[16, 16]" class="align-items-end">
          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="calendar" class="action-icon" />
                {{ $t("recette.anneeScolaire") }}
              </label>
              <a-select
                v-model="selectedSchoolarYears"
                :placeholder="$t('recette.anneeScolaire')"
                class="filter-select"
                @change="handleSchoolarYearFilter"
                mode="multiple"
                allowClear
              >
                <a-select-option
                  v-for="sy in settings.schoolarYears"
                  :key="sy._id"
                  :value="sy._id"
                >
                  {{ schoolarYearNameById[sy._id] || sy.name }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>

          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="credit-card" class="action-icon" />
                {{ $t("encaissement.paymentMethod") }}
              </label>
              <a-select
                v-model="selectedPaymentMethod"
                :placeholder="$t('recette.modePaiement')"
                class="filter-select"
                @change="handlePaymentMethodFilter"
                mode="multiple"
                allowClear
              >
                <a-select-option value="ESPESE">{{
                  $t("encaissement.cash")
                }}</a-select-option>
                <a-select-option value="CHEQUE">{{
                  $t("encaissement.cheque")
                }}</a-select-option>
                <a-select-option value="TPE">{{
                  $t("encaissement.tpe")
                }}</a-select-option>
                <a-select-option value="TRAITE">{{
                  $t("encaissement.draft")
                }}</a-select-option>
                <a-select-option value="VIREMENT">{{
                  $t("encaissement.transfer")
                }}</a-select-option>
              </a-select>
            </div>
          </a-col>

          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="bank" class="action-icon" />
                {{ $t("encaissement.cashRegister") }}
              </label>
              <a-select
                v-model="selectedCaisse"
                :placeholder="$t('encaissement.selectCashRegister')"
                class="filter-select"
                dropdownClassName="caisse-filter-dropdown"
                option-label-prop="label"
                showSearch
                :filter-option="filterCaisseOption"
                @change="handleCaisseFilter"
                mode="multiple"
                allowClear
              >
                <a-select-option
                  v-for="caisse in availableCaisses"
                  :key="caisse._id"
                  :value="caisse._id"
                  :label="caisse.name"
                  :title="caisse.name"
                >
                  <div class="caisse-option">
                    <span class="caisse-option-name">{{ caisse.name }}</span>
                    <a-tag
                      class="caisse-option-tag"
                      :color="caisseTypeColor(caisse.type)"
                    >
                      {{ $t(`caisse.${caisse.type}`) }}
                    </a-tag>
                  </div>
                </a-select-option>
              </a-select>
            </div>
          </a-col>

          <a-col :xs="24" :sm="8" :md="8" :lg="8">
            <div class="filter-item">
              <label class="filter-label">
                <a-icon type="calendar" class="action-icon" />
                {{ $t("encaissement.period") }}
                <a-tooltip
                  placement="top"
                  overlayClassName="period-hint-tooltip"
                >
                  <template slot="title">
                    {{ $t("recette.periodeHint") }}
                  </template>
                  <a-icon type="info-circle" class="period-hint-icon" />
                </a-tooltip>
              </label>
              <a-range-picker
                :placeholder="[
                  $t('paiement.dateDebut'),
                  $t('paiement.dateFin'),
                ]"
                format="DD/MM/YYYY"
                v-model="dateRangeData"
                :mode="mode"
                @change="handleDateRange"
                class="date-picker"
              />
            </div>
          </a-col>
        </a-row>
      </div>
      <!-- Legend for Facture and Reçu -->
      <div class="margin-bottom-16">
        <span class="margin-right-8">{{ $t("finance.legend") }}</span>
        <a-tag color="#108ee9" class="type-tag">{{
          $t("recette.factureTag")
        }}</a-tag>
        <a-tag color="blue" class="type-tag">{{ $t("recette.recus") }}</a-tag>
      </div>

      <div class="table-container">
        <a-table
          @change="tableChanged"
          rowKey="_id"
          :pagination="tablePagination"
          :data-source="data"
          :columns="columns"
          :loading="tableLoading"
          :scroll="{ x: 'max-content' }"
          :row-class-name="getRowClassName"
        >
          <template slot="serial" slot-scope="text, record">
            <a-tag :color="getSerialColor(record)">{{ text }}</a-tag>
            <br />
            <a-tag
              v-if="record.cancellation"
              :color="getCancellationColor(record.cancellation.type)"
              >{{ record.cancellation.type }}</a-tag
            >
          </template>
          <template slot="typeReglement" slot-scope="text, record">
            <li v-for="i in record.details" :key="i._id">
              {{ i.typeReglement }}
            </li>
          </template>
          <template slot="studentName" slot-scope="text, record">
            <li v-for="i in record.details" :key="i._id">
              {{ i.studentName }}
            </li>
          </template>
          <template slot="paymentMethod" slot-scope="text, record">
            <div v-if="Array.isArray(record.paymentMethod)">
              <a-tooltip :title="$t('recette.detailsPaymentDist')">
                <a-button
                  type="primary"
                  shape="circle"
                  size="small"
                  @click="chequeDetailsModal(record)"
                  style="margin-right: 6px"
                >
                  <a-icon type="file-search" />
                </a-button>
              </a-tooltip>
              <span v-for="method in record.paymentMethod" :key="method">
                <template>
                  <a-tag
                    :color="getPaymentMethodColor(method)"
                    style="margin-bottom: 4px"
                  >
                    {{ $t(`recette.paymentMethod.${method}`) }}
                  </a-tag>
                </template>
              </span>
            </div>

            <div v-else>{{ text }}</div>
          </template>
          <template slot="date" slot-scope="text, record">
            {{ moment(record.date).format("DD/MM/YYYY") }}
          </template>
          <template slot="createdAt" slot-scope="text, record">
            {{
              record.createdAt
                ? moment(record.createdAt).format("DD/MM/YYYY HH:mm")
                : "--"
            }}
          </template>
          <div
            slot="filterDropdown"
            slot-scope="{
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
              column,
            }"
            style="padding: 8px"
          >
            <a-input
              v-ant-ref="(c) => (searchInput = c)"
              :placeholder="`${$t('paiement.chercher')} ${column.title}`"
              :value="selectedKeys[0]"
              style="width: 188px; margin-bottom: 8px; display: block"
              @change="
                (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
              "
              @pressEnter="
                () => handleSearch(selectedKeys, confirm, column.dataIndex)
              "
            />
            <a-button
              type="primary"
              icon="search"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="
                () => handleSearch(selectedKeys, confirm, column.dataIndex)
              "
              >{{ $t("scolarite.recherche") }}</a-button
            >
            <a-button
              size="small"
              style="width: 90px"
              @click="() => handleReset(clearFilters)"
              >{{ $t("action.reinitialiser") }}</a-button
            >
          </div>
          <a-icon
            slot="filterIcon"
            slot-scope="filtered"
            type="search"
            :style="{ color: filtered ? '#108ee9' : undefined }"
          />
          <template
            slot="customRender"
            slot-scope="text, record, index, column"
          >
            <span v-if="searchText && searchedColumn === column.dataIndex">
              <template
                v-for="(fragment, i) in text
                  .toString()
                  .split(
                    new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i')
                  )"
              >
                <mark
                  v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                  :key="i"
                  class="highlight"
                  >{{ fragment }}</mark
                >
                <template v-else>{{ fragment }}</template>
              </template>
            </span>
            <template v-else>{{ text }}</template>
          </template>
          <a-tag color="#2db7f5" slot="userName" slot-scope="text, record">
            {{ record.doneBy.userName }}
          </a-tag>

          <template slot="caisseName" slot-scope="text, record">
            <span v-if="record.caisse">
              <a-icon type="bank" class="action-icon" />
              {{ record.caisse.name }}
              <br />
              <div class="transfer-status">
                <a-tag
                  :color="record.caisseTransfer ? 'green' : 'orange'"
                  class="transfer-tag"
                >
                  <a-icon
                    :type="
                      record.caisseTransfer ? 'check-circle' : 'clock-circle'
                    "
                    class="status-icon"
                  />
                  {{
                    record.caisseTransfer
                      ? $t("recette.transfere")
                      : $t("recette.nonTransfere")
                  }}
                </a-tag>
              </div>
            </span>
            <span v-else>--</span>
          </template>
          <template slot="montantHT" slot-scope="text, record">
            <span class="amount-cell">
              {{ record.montantHT ? record.montantHT.toFixed(2) : "0.00" }}
            </span>
          </template>
          <template slot="montantTotal" slot-scope="text, record">
            <span class="amount-cell">
              {{
                record.montantTotal ? record.montantTotal.toFixed(2) : "0.00"
              }}
            </span>
          </template>
          <template slot="actions" slot-scope="text, record">
            <div class="row-actions">
              <a-tooltip :title="$t('recette.detailsReglement')">
                <a-button shape="circle" size="small" @click="openDetails(record)">
                  <a-icon type="eye" />
                </a-button>
              </a-tooltip>
              <a-tooltip :title="$t('action.imprimer')">
                <a-button
                  type="primary"
                  shape="circle"
                  size="small"
                  @click="() => print(record)"
                >
                  <a-icon type="file-pdf" />
                </a-button>
              </a-tooltip>
              <a-tooltip :title="$t('recette.annulation')">
                <a-button
                  type="danger"
                  shape="circle"
                  size="small"
                  :loading="loadingAnnulation"
                  :disabled="loadingAnnulation || record.status == 'cancelled'"
                  @click="
                    () => {
                      selectedRecord = record;
                      // Set default cancelType based on record type
                      cancelType = record.typeFR === 'F' ? 'Avoir' : 'Annulation';
                      visibleCancelModal = true;
                    }
                  "
                >
                  <a-icon type="delete" />
                </a-button>
              </a-tooltip>
            </div>
          </template>
        </a-table>
      </div>
    </div>
    <chequeModal
      v-if="chequeVisible"
      :chequeVisible="chequeVisible"
      :invoiceId="invoiceId"
      @close="handleClose"
    />
    <a-drawer
      :title="$t('recette.detailsReglement')"
      :visible="detailsVisible"
      :width="720"
      destroy-on-close
      @close="detailsVisible = false"
    >
      <template v-if="detailsRecord">
        <a-divider orientation="left">
          <a-icon type="info-circle" /> {{ $t("recette.infosGenerales") }}
        </a-divider>
        <a-descriptions bordered :column="2" size="small">
          <a-descriptions-item :label="$t('recette.numSerie')">
            <a-tag :color="getSerialColor(detailsRecord)">
              {{ detailsRecord.serial }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.type')">
            <a-tag :color="detailsRecord.typeFR === 'F' ? '#108ee9' : 'blue'">
              {{
                detailsRecord.typeFR === "F"
                  ? $t("recette.factureTag")
                  : $t("recette.recus")
              }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.nom')">
            {{ (detailsRecord.addressedTo && detailsRecord.addressedTo.name) || "--" }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="detailsRecord.addressedTo && detailsRecord.addressedTo.MF"
            label="MF"
          >
            {{ detailsRecord.addressedTo.MF }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.datePaiement')">
            {{ moment(detailsRecord.date).format("DD/MM/YYYY") }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.dateAjout')">
            {{
              detailsRecord.createdAt
                ? moment(detailsRecord.createdAt).format("DD/MM/YYYY HH:mm")
                : "--"
            }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.anneeScolaire')">
            {{ schoolarYearNameById[detailsRecord.schoolarYear] || "--" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.doneBy')">
            {{ (detailsRecord.doneBy && detailsRecord.doneBy.userName) || "--" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('encaissement.cashRegister')">
            <span v-if="detailsRecord.caisse">
              {{ detailsRecord.caisse.name }}
              <a-tag
                :color="detailsRecord.caisseTransfer ? 'green' : 'orange'"
                class="transfer-tag"
              >
                {{
                  detailsRecord.caisseTransfer
                    ? $t("recette.transfere")
                    : $t("recette.nonTransfere")
                }}
              </a-tag>
            </span>
            <span v-else>--</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.modePaiement')">
            <a-tag
              v-for="method in detailsRecord.paymentMethod || []"
              :key="method"
              :color="getPaymentMethodColor(method)"
            >
              {{ $t(`recette.paymentMethod.${method}`) }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <a-alert
          v-if="detailsRecord.cancellation"
          type="error"
          show-icon
          class="margin-top-16"
        >
          <template slot="message">
            {{ $t("recette.annulation") }} :
            {{ detailsRecord.cancellation.type }}
          </template>
          <template slot="description">
            <div v-if="detailsRecord.cancellation.money">
              {{ $t("recette.montant") }} :
              {{ detailsRecord.cancellation.money.toFixed(2) }}
            </div>
            <div v-if="detailsRecord.cancellation.cancelledBy">
              {{ $t("recette.cancelledBy") }} :
              {{ detailsRecord.cancellation.cancelledBy }}
            </div>
            <div v-if="detailsRecord.cancellation.note">
              {{ $t("paiement.note") }} : {{ detailsRecord.cancellation.note }}
            </div>
          </template>
        </a-alert>

        <a-divider orientation="left">
          <a-icon type="calculator" /> {{ $t("recette.repartitionMontants") }}
        </a-divider>
        <a-descriptions bordered :column="2" size="small">
          <a-descriptions-item :label="$t('recette.totalSansReduction')">
            {{ formatAmount(detailsRecord.montantHtBrut) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.montantHTVA')">
            {{ formatAmount(detailsRecord.montantHT) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.tva')">
            {{ formatAmount(detailsRecord.montantTVA) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.montantTTC')">
            <b>{{ formatAmount(detailsRecord.montantTotal) }}</b>
          </a-descriptions-item>
          <a-descriptions-item
            v-for="entry in amountBreakdown(detailsRecord)"
            :key="entry.key"
            :label="$t(entry.label)"
          >
            {{ formatAmount(entry.value) }}
          </a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left">
          <a-icon type="profile" /> {{ $t("recette.lignesReglement") }}
        </a-divider>
        <table class="table table-striped">
          <thead>
            <th>{{ $t("profil.student") }}</th>
            <th>{{ $t("recette.typeReglement") }}</th>
            <th>{{ $t("recette.paiementQuoi") }}</th>
            <th>{{ $t("recette.montantHTVA") }}</th>
            <th>{{ $t("recette.montantTTC") }}</th>
          </thead>
          <tbody>
            <tr
              v-for="(detail, index) in detailsRecord.details || []"
              :key="index"
            >
              <td>{{ detail.studentName }}</td>
              <td>{{ detail.typeReglement }}</td>
              <td>
                <a-tag
                  v-for="(label, i) in detailFeeLabels(detail)"
                  :key="i"
                  color="blue"
                  class="fee-tag"
                >
                  {{ label }}
                </a-tag>
                <span v-if="!detailFeeLabels(detail).length">--</span>
              </td>
              <td>{{ formatAmount(detail.montantHT) }}</td>
              <td>{{ formatAmount(detail.montantTotal) }}</td>
            </tr>
          </tbody>
        </table>
      </template>
    </a-drawer>
    <a-modal
      v-model="visibleCancelModal"
      :title="$t('paiement.annulation')"
      @ok="annulation(selectedRecord)"
    >
      <a-label>{{ $t("recette.type") }} : </a-label>
      <div class="text-center">
        <span class="ml-2 mr-2">{{ $t("paiement.avoir") }}</span>
        <a-switch
          v-if="!isFacture"
          :class="cancelType == 'Avoir' ? 'yellow-switch' : 'red-switch'"
          :checked="cancelType === 'Annulation'"
          @change="
            (val) => {
              cancelType = val ? 'Annulation' : 'Avoir';
            }
          "
        />
        <a-switch v-else :class="'yellow-switch'" :checked="false" disabled />
        <span class="ml-2 mr-2">{{ $t("paiement.annulation") }}</span>
      </div>
      <div v-if="isFacture" style="margin-top: 8px; text-align: center">
        <a-alert
          :message="$t('encaissement.invoicesCanOnlyBeCredited')"
          type="info"
          show-icon
          style="margin-bottom: 16px"
        />
      </div>
      <a-label>{{ $t("paiement.note") }} : </a-label>
      <a-textarea
        v-model="cancelNote"
        :placeholder="$t('paiement.note')"
        :auto-size="{ minRows: 3, maxRows: 5 }"
      />
    </a-modal>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapGetters } from "vuex";
import apiClient from "@/services/axios";
import Vue from "vue";
import VueRouter from "vue-router";
import moment from "moment";
import {
  formatFeeName,
  getPaymentMethodColor,
  getSerialColor,
  getCancellationColor,
  shortSchoolarYearName,
} from "@/utilities/settings";
import chequeModal from "@/components/comptabilite/chequeModal";
import { printInvoice } from "@/utilities/printInvoice";
import { generateEtatDesRecettesPdf } from "./etatDesRecettesPdf";
import {
  formatCurrency as formatCurrencyUtil,
  getBuildingCurrency,
} from "@/utilities/countrySpecific";

Vue.use(VueRouter);

export default {
  computed: {
    ...mapState(["settings", "user"]),
    ...mapGetters("settings", ["btnAccessGetter"]),
    isFacture() {
      return this.selectedRecord?.typeFR === "F";
    },
    tablePagination() {
      return {
        ...this.pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
      };
    },
    schoolarYearNameById() {
      return (this.settings.schoolarYears || []).reduce((acc, sy) => {
        acc[sy._id] = shortSchoolarYearName(sy.name);
        return acc;
      }, {});
    },
    columns() {
      return [
        //numéro de série
        {
          title: this.$t("recette.numSerie"),
          dataIndex: "serial",
          key: "serial",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "serial",
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("profil.student"),
          dataIndex: "studentName",
          key: "studentName",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "studentName",
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("recette.typeReglement"),
          dataIndex: "typeReglement",
          key: "typeReglement",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "typeReglement",
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },
        {
          title: this.$t("recette.modePaiement"),
          dataIndex: "paymentMethod",
          key: "paymentMethod",
          scopedSlots: {
            customRender: "paymentMethod",
          },
        },
        {
          title: this.$t("recette.montantHTVA"),
          dataIndex: "montantHT",
          key: "montantHT",
          sorter: true,
          scopedSlots: { customRender: "montantHT" },
        },
        {
          title: this.$t("recette.montantTTC"),
          dataIndex: "montantTotal",
          key: "montantTotal",
          sorter: true,
          scopedSlots: { customRender: "montantTotal" },
        },
        {
          title: this.$t("recette.anneeScolaire"),
          dataIndex: "schoolarYear",
          key: "schoolarYear",
          customRender: (text, record) =>
            this.schoolarYearNameById[record.schoolarYear] || "--",
        },
        {
          title: this.$t("recette.datePaiement"),
          dataIndex: "date",
          key: "date",
          sorter: true,
          scopedSlots: {
            customRender: "date",
          },
        },
        {
          title: this.$t("recette.dateAjout"),
          dataIndex: "createdAt",
          key: "createdAt",
          sorter: true,
          scopedSlots: {
            customRender: "createdAt",
          },
        },
        {
          title: this.$t("encaissement.cashRegister"),
          dataIndex: "caisseName",
          key: "caisseName",
          scopedSlots: {
            customRender: "caisseName",
          },
        },
        {
          title: this.$t("recette.doneBy"),
          dataIndex: "userName",
          key: "userName",
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
            customRender: "userName",
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              });
            }
          },
        },

        {
          title: this.$t("recette.actions"),
          dataIndex: "actions",
          key: "actions",
          fixed: "right",
          width: 140,
          align: "center",
          scopedSlots: {
            customRender: "actions",
          },
        },
      ];
    },
  },
  components: {
    chequeModal,
  },
  async created() {
    this.currency = getBuildingCurrency();
    this.$gtag.pageview({
      page_path: this.$route.path,
      page_location: window.location.href,
      page_title: document.title + " - Liste de règlements",
    });
    this.nomClient = "--";
    // Defaults to the active school year; the user can add more years to the filter.
    const activeSchoolarYear = this.settings.activeSchoolarYear;
    this.selectedSchoolarYears = activeSchoolarYear ? [activeSchoolarYear] : [];
    this.getInvoices();
    this.getAvailableCaisses();
  },

  data() {
    return {
      loadedChequeData: null,
      dateRangeData: ["", ""],
      mode: ["date", "date"],
      fields: [
        "serial",
        "nomClient",
        "typeReglement",
        "paiementSur",
        "paymentMethod",
        "montantHT",
        "montantTotal",
        "datePaiement",
      ],
      editingKey: "",
      rowData: [],
      tableLoading: true,
      nomClient: "",
      visibleParentFile: false,
      data: [],
      parent: {},
      chequeVisible: false,
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      targetKeys: [],
      indeterminate: true,
      filtredTable: [],
      filtredTableCache: [],
      printLoading: false,
      selectedRecord: null,
      visibleCancelModal: false,
      cancelType: "Annulation",
      cancelNote: "",
      loadingAnnulation: false,
      selectedPaymentMethod: [],
      selectedCaisse: [],
      selectedSchoolarYears: [],
      availableCaisses: [],
      currency: null,
      detailsVisible: false,
      detailsRecord: null,
      columnFilters: {},
      sortField: "date",
      sortOrder: "descend",
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
      },
    };
  },
  methods: {
    formatCurrency(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    formatFeeName,
    getPaymentMethodColor,
    getSerialColor,
    getCancellationColor,

    /** Filters are resolved server side so they apply to the whole result set, not just the page. */
    buildQueryParams() {
      const params = {};

      if (this.selectedSchoolarYears.length) {
        params.schoolarYear = this.selectedSchoolarYears.join(",");
      }
      if (this.selectedPaymentMethod.length) {
        params.paymentMethod = this.selectedPaymentMethod.join(",");
      }
      if (this.selectedCaisse.length) {
        params.caisse = this.selectedCaisse.join(",");
      }
      if (this.dateRangeData && this.dateRangeData[0] && this.dateRangeData[1]) {
        params.dateFrom = moment(this.dateRangeData[0]).format("YYYY-MM-DD");
        params.dateTo = moment(this.dateRangeData[1]).format("YYYY-MM-DD");
      }

      ["serial", "studentName", "typeReglement", "doneBy"].forEach((key) => {
        const value = (this.columnFilters[key] || [])[0];
        if (value) params[key] = value;
      });

      params.sortField = this.sortField;
      params.sortOrder = this.sortOrder;

      return params;
    },
    async getInvoices() {
      this.tableLoading = true;
      try {
        const res = await apiClient.get(
          "/compV3/parent-invoice/etat-des-recettes",
          {
            params: {
              ...this.buildQueryParams(),
              page: this.pagination.current,
              pageSize: this.pagination.pageSize,
            },
          }
        );
        const { data, total, page, pageSize } = res.data;
        this.data = data || [];
        this.rowData = this.data;
        this.filtredTable = this.data;
        this.filtredTableCache = this.filtredTable;
        this.pagination.total = total || 0;
        this.pagination.current = page || 1;
        this.pagination.pageSize = pageSize || this.pagination.pageSize;
      } catch (err) {
        console.error("Could not fetch invoices:", err);
      } finally {
        this.tableLoading = false;
      }
    },
    reloadFromFirstPage() {
      this.pagination.current = 1;
      this.getInvoices();
    },
    async getAvailableCaisses() {
      await apiClient
        .get("/compV3/caisse/sous-caisses")
        .then((res) => {
          this.availableCaisses = this.sortCaissesByType(res.data || []);
        })
        .catch((err) => {
          console.error("Could not fetch available caisses:", err);
        });
    },
    sortCaissesByType(caisses) {
      const order = { PRINCIPAL: 0, SUBCAISSE: 1, BANK: 2 };
      return [...caisses].sort((a, b) => {
        const typeDiff = (order[a.type] ?? 9) - (order[b.type] ?? 9);
        if (typeDiff !== 0) return typeDiff;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    },
    caisseTypeColor(type) {
      if (type === "PRINCIPAL") return "geekblue";
      if (type === "BANK") return "green";
      return "orange";
    },
    filterCaisseOption(input, option) {
      const search = (input || "").toLowerCase();
      const caisse = this.availableCaisses.find(
        (item) => String(item._id) === String(option.key || option.value)
      );
      if (!caisse) return true;
      const typeLabel = this.$t(`caisse.${caisse.type}`) || "";
      return (
        String(caisse.name || "")
          .toLowerCase()
          .includes(search) || String(typeLabel).toLowerCase().includes(search)
      );
    },
    getCaisseName(record) {
      return record.caisse && record.caisse.name ? record.caisse.name : "--";
    },
    /** Fee keys of a detail line turned into the labels shown in the drawer. */
    detailFeeLabels(detail) {
      if (!detail || !detail.fees) return [];
      return Object.keys(detail.fees).map((key) =>
        formatFeeName(key, detail.daily, this.$t.bind(this))
      );
    },
    formatAmount(value) {
      return Number(value || 0).toFixed(2);
    },
    /** Only the payment methods actually used are worth a row in the drawer. */
    amountBreakdown(record) {
      return [
        { key: "montantEspece", label: "recette.montantEspece" },
        { key: "montantCheque", label: "recette.montantCheque" },
        { key: "montantTPE", label: "recette.montantTPE" },
        { key: "montantTraite", label: "recette.montantTraite" },
        { key: "montantVirement", label: "recette.montantVirement" },
      ]
        .filter((entry) => record[entry.key])
        .map((entry) => ({ ...entry, value: record[entry.key] }));
    },
    openDetails(record) {
      this.detailsRecord = record;
      this.detailsVisible = true;
    },
    async imprimer() {
      this.printLoading = true;

      this.$gtag.event("Imp etatDesRecettes", {
        event_category: "Impression PDF",
        event_label: "comptabilite:etatDesRecettes",
        value: 1,
      });

      try {
        // The table is paginated, so the export refetches every matching row.
        const res = await apiClient.get(
          "/compV3/parent-invoice/etat-des-recettes",
          { params: { ...this.buildQueryParams(), all: true } }
        );
        const caisseById = (this.availableCaisses || []).reduce((acc, caisse) => {
          acc[String(caisse._id)] = caisse.name;
          return acc;
        }, {});
        const selectedCaisseNames = this.selectedCaisse
          .map((id) => caisseById[String(id)])
          .filter(Boolean);
        const isAllCaisses = !this.selectedCaisse.length;
        const caisseNames = [...new Set(selectedCaisseNames)];
        await generateEtatDesRecettesPdf(
          res.data.data || [],
          this.settings,
          this.dateRangeData,
          this.$t.bind(this),
          this.$i18n.locale,
          this.schoolarYearNameById,
          caisseNames,
          isAllCaisses
        );
      } catch (err) {
        console.error("could not print etat de recette: ", err);
        this.$message.error(
          err.message || "Une erreur est survenue lors de l'impression"
        );
      } finally {
        this.printLoading = false;
      }
    },

    handleClose() {
      this.chequeVisible = false;
    },
    handleDateRange(value) {
      this.dateRangeData = value;
      this.reloadFromFirstPage();
    },
    handlePaymentMethodFilter(value) {
      this.selectedPaymentMethod = value;
      this.reloadFromFirstPage();
    },
    handleCaisseFilter(value) {
      this.selectedCaisse = value;
      this.reloadFromFirstPage();
    },
    handleSchoolarYearFilter(value) {
      this.selectedSchoolarYears = value;
      this.reloadFromFirstPage();
    },
    chequeDetailsModal(record) {
      this.invoiceId = record._id;
      this.chequeVisible = true;
    },
    tableChanged(pagination, filters, sorter) {
      const nextFilters = filters || {};
      const filtersChanged =
        JSON.stringify(nextFilters) !== JSON.stringify(this.columnFilters);
      this.columnFilters = nextFilters;

      // Clearing the sort falls back to the newest invoices first.
      const nextField = sorter && sorter.order ? sorter.field : "date";
      const nextOrder = sorter && sorter.order ? sorter.order : "descend";
      const sortChanged =
        nextField !== this.sortField || nextOrder !== this.sortOrder;
      this.sortField = nextField;
      this.sortOrder = nextOrder;

      if (pagination) {
        this.pagination.pageSize = pagination.pageSize;
        // A new filter or sort invalidates the current page number.
        this.pagination.current =
          filtersChanged || sortChanged ? 1 : pagination.current;
      }
      this.getInvoices();
    },
    moment,

    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },
    async print(record) {
      this.$gtag.event("Imp one multi reglement", {
        event_category: "Impression PDF",
        event_label: "ProfileEleve:listeReglement",
        value: 1,
      });
      let toPrint = record;
      try {
        if (record?._id) {
          const res = await apiClient.get(
            `/compV3/parent-invoice/${record._id}`
          );
          if (res.data) toPrint = res.data;
        }
      } catch (e) {
        console.log(e);
      }
      await printInvoice(toPrint, this.settings, this.user);
    },

    annulation(record) {
      this.loadingAnnulation = true;

      apiClient
        .post("/compV3/parent-invoice/cancel/" + record._id, {
          type: this.cancelType,
          note: this.cancelNote,
        })
        .then((res) => {
          this.getInvoices();
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.errorCode
          ) {
            this.$message.error(
              this.$t(`errorCode.${err.response.data.errorCode}`)
            );
          } else {
            this.$message.error(this.$t("error.erreur"));
          }
        })
        .finally(() => {
          this.loadingAnnulation = false;
          this.visibleCancelModal = false;
        });
    },

    getRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },
  },
};
</script>

<style>
@import "../../common.css";

/* Component-specific enhancements */
.form-label {
  font-weight: 500;
  color: #6c757d;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

/* Ant Design component improvements */
.ant-btn {
  border-radius: 6px;
  font-weight: 500;
}

.ant-btn-primary {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border: none;
}

.ant-btn-primary:hover {
  background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}

.ant-select {
  border-radius: 6px;
}

.ant-select-focused .ant-select-selector {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.ant-picker {
  border-radius: 6px;
}

.ant-picker-focused {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* Additional utility classes */
.margin-right-8 {
  margin-right: 8px;
}

.margin-top-16 {
  margin-top: 16px;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.caisse-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.caisse-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.caisse-option-tag {
  margin-right: 0;
  flex-shrink: 0;
}

.fee-tag {
  margin: 0 4px 4px 0;
}

.period-hint-icon {
  margin-left: 6px;
  color: #1890ff;
  cursor: help;
  font-size: 13px;
}
</style>

<style>
.caisse-filter-dropdown .ant-select-dropdown-menu-item {
  padding-top: 8px;
  padding-bottom: 8px;
}

.period-hint-tooltip {
  max-width: 280px;
}
</style>
